import React from "react"
import {compose} from "redux"
import {useTranslation} from "react-i18next"
import {RouteComponentProps} from "react-router"
import {useMediaQuery, useTheme} from "@material-ui/core"

// HOCs
import {WithUserProps} from "../../../../auth/hoc/withUser"
import {WithRecordsProps} from "../../../hoc/withRecords"
import {WithRecordDataProps} from "../../../hoc/withRecordData"
import withRecordSignals, {WithRecordSignalsProps} from "../../../hoc/withRecordSignals"
import withApproveDialog, {WithApproveDialogProps} from "../../../hoc/withApproveDialog"

// Context
import {useUiSkin} from "../../../../ui-skin/context/UiSkinContext"

// Components
import ModernAppBar from "../../../../../components/modern/ModernAppBar"
import ModernEcgStage from "../ModernEcgStage"
import ModernApproveDialog from "../ModernApproveDialog"
import ModernRecordToolbar from "../ModernRecordToolbar"
import ModernPatientCard from "../ModernPatientCard"
import ModernClinicalDialog from "../ModernClinicalDialog"
import ModernParamsCard, {ModernParamsApi, ParameterKey} from "../ModernParamsCard"
import ModernDispoCard, {ModernDispoApi} from "../ModernDispoCard"
import ModernMeasureBar from "../ModernMeasureBar"
import ModernEcgPager from "../ModernEcgPager"
import ModernKeysBar from "../ModernKeysBar"
import ModernRecordMobile, {ModernMobileTab} from "../ModernRecordMobile"
import ModernBackButton from "../ModernBackButton"
import AnnotationView from "../../../../annotation/components/AnnotationView"
import useRecordAnnotation from "../../../../annotation/useRecordAnnotation"
import {EcgMeasurement, ModernCaliperApi} from "../ModernCaliperLayer"

// Geometry
import {EcgLayoutKey} from "../ModernEcgGrid/geometry"
import {clampWindowStart, ecgWindowInfo, legacyWindowSignals, stepWindowStart} from "../ModernEcgGrid/window"
import {longRecordsEnabled} from "../longRecordPilot"

// Hooks
import useRecordShortcuts from "../useRecordShortcuts"
import useEcgZoom from "../useEcgZoom"
import {isPediatric} from "../patientFields"

// Constants
import {AppRoute} from "../../../../routing/constants"

// Models
import {RecordTypeEnum} from "../../../models"
import {RecordStatusEnum} from "../../../../doctor-stats/models"

// Utils
import Utils from "../../../../../utils/Utils"

// Styles
import useStyles from "./styles"
const CLINICAL_SETTLE_MS = 500;
const CLINICAL_DISMISS_PX = 24;


type ModernRecordPageProps =
    RouteComponentProps &
    WithUserProps &
    WithRecordsProps &
    WithRecordDataProps &
    WithRecordSignalsProps &
    WithApproveDialogProps

/**
 * The Modern record review screen. One component serves BOTH routes:
 * `/record` (live review) and `/preview/:id` (read only). Read-only is derived
 * from the route rather than from a prop so the two containers stay untouched.
 *
 * This page owns the display state the toolbar sets — layout, speed, gain,
 * calipers, sidebar, panel — because several of those affect more than the
 * trace: `sidebar` restructures the whole page, and `panelOpen` changes the
 * stage's height. The ECG renderer itself is still the shared Plotly grid; this
 * component builds the chrome around it and does not fork the renderer.
 *
 * @param props
 */
function ModernRecordPage(props: ModernRecordPageProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {toggleTheme} = useUiSkin();
    const {
        user,
        match,
        history,
        metaData,
        patientInfo,
        signals,
        signalsFetching,
        currentRecord,
        fetchingRecord,
        setApproveDialogActive,
        setReceiveNext,
    } = props;

    const [panelOpen, setPanelOpen] = React.useState(true);
    // false = info strip across the top; true = left rail + disposition dock
    const [sidebar, setSidebar] = React.useState(false);
    const [layout, setLayout] = React.useState<EcgLayoutKey>("12x1");
    const [speed, setSpeed] = React.useState(25);   // mm/s
    const [gain, setGain] = React.useState(10);     // mm/mV
    const [windowStartSec, setWindowStartSec] = React.useState(0);
    const [caliperOn, setCaliperOn] = React.useState(false);
    const [zoom, setZoom] = React.useState(1);
    const [measurement, setMeasurement] = React.useState<EcgMeasurement | null>(null);
    const [clinicalOpen, setClinicalOpen] = React.useState(false);
    const [clinicalAuto, setClinicalAuto] = React.useState(false);
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [mobileTab, setMobileTab] = React.useState<ModernMobileTab>("conclusion");

    const dispoApiRef = React.useRef<ModernDispoApi | null>(null);
    const paramsApiRef = React.useRef<ModernParamsApi | null>(null);
    const caliperApiRef = React.useRef<ModernCaliperApi | null>(null);
    const clinicalShownForRef = React.useRef<string | null>(null);
    const ecgBoxRef = React.useRef<HTMLDivElement | null>(null);

    /*
     * Wheel-to-zoom and drag-to-pan for the trace.
     *
     * Panning is handed over to the calipers while they are on — a drag there
     * is a measurement, and the two gestures are the same one.
     */
    const ecgZoom = useEcgZoom({
        zoom: zoom,
        setZoom: setZoom,
        boxRef: ecgBoxRef,
        panEnabled: false === caliperOn,
    });

    /*
     * Unsaved-edit tracking, restoring the guard Classic had.
     *
     * Classic disabled Approve with `disabled={isConclusionEditing ||
     * isParameterEditing}`. The Modern cards autosave instead of using an
     * explicit edit/save mode, so they deliberately never set those shared redux
     * flags — which left approval completely unguarded: hitting Ctrl+Enter with
     * a half-typed conclusion put the conclusion PATCH and the approve PATCH in
     * flight against the same record at once, with no ordering guarantee. A
     * write landing after the approval is exactly what the Classic guard existed
     * to prevent.
     */
    const [dispoBusy, setDispoBusy] = React.useState(false);
    const [paramsBusy, setParamsBusy] = React.useState(false);
    /** True while the findings field is showing a whole report — see renderDispo. */
    const [findingsExpanded, setFindingsExpanded] = React.useState(false);
    const editsPending = dispoBusy || paramsBusy;

    /*
     * Survives the unmount/remount that toggling the sidebar causes.
     *
     * `renderDispo("card")` and `renderDispo("dock")` sit at different points in
     * the tree, so flipping `sidebar` tears the card down and builds a new one.
     * Without this cache the remount re-seeded its textarea from the store —
     * which still held the PREVIOUS conclusion while the flush dispatched on
     * unmount was in flight — so the reviewer saw stale text, and if that PATCH
     * failed the typed text was gone with no way back.
     */
    const conclusionDraftRef = React.useRef<{recordId: string | null, text: string} | null>(null);

    const readOnly = match.path === AppRoute.RecordPreview;
    const isEditable =
        !readOnly &&
        false === Utils.isCompanyMaster(user) &&
        currentRecord !== null &&
        currentRecord.status !== RecordStatusEnum.APPROVED;
    const canApprove = isEditable && currentRecord !== null && currentRecord.recordType !== RecordTypeEnum.HOLTER;

    const recordKey = currentRecord !== null ? (currentRecord.teleRecordId || currentRecord.ecgId || null) : null;
    const hasClinicalInfo = currentRecord !== null && Boolean(currentRecord.metaData);

    /**
     * Annotation mode. The labels are the analysis service's, so the button
     * only appears once a record actually has some — otherwise it would open an
     * empty screen and look broken.
     */
    const [annotationOn, setAnnotationOn] = React.useState(false);

    /*
     * The annotation, from the server and back, at the record's own sample
     * rate (useRecordAnnotation): nothing is converted, edited or saved until
     * every lead agrees on one; edits are saved once the server confirms them,
     * retried when it does not, and never dropped on the way out.
     */
    const record = useRecordAnnotation({
        recordKey: recordKey,
        ecgId: currentRecord !== null ? (currentRecord.ecgId || null) : null,
        recordName: currentRecord !== null ? (currentRecord.fileName || null) : null,
        signals: signals || null,
        on: annotationOn,
    });
    const annotation = record.annotation;
    const outOfOrderReviewed = record.outOfOrderReviewed;

    /** Annotation work the server does not have yet: approve waits for it. */
    const annotationUnsaved = record.unsaved || record.failing || record.refused;

    /** Why approve is blocked by the annotation, if it is. */
    const annotationBlock = outOfOrderReviewed > 0
        ? t("annotation.order.approve_blocked", {count: outOfOrderReviewed})
        : record.refused
            ? t("annotation.save_state.approve_refused", {status: record.saveStatus})
        : record.failing
            ? t("annotation.save_state.approve_failing")
            : record.unsaved
                ? t("annotation.save_state.approve_saving")
                : null;

    /* Leaving a record must not carry its state into the next one. */
    React.useEffect(() => {
        setAnnotationOn(false)
    }, [recordKey]);

    /* leaving annotation sends what is pending at once */
    const toggleAnnotation = React.useCallback(() => {
        if (annotationOn) {
            record.flush()
        }

        setAnnotationOn(on => !on)
    }, [annotationOn, record]);

    const longRecords = longRecordsEnabled(user, signals || []);
    const stageSignals = React.useMemo(
        () => (longRecords ? (signals || []) : legacyWindowSignals(signals || [])),
        [signals, longRecords],
    );
    const windowInfo = React.useMemo(
        () => ecgWindowInfo(stageSignals, longRecords),
        [stageSignals, longRecords],
    );
    const durationSec = windowInfo.totalSec > 0 ? windowInfo.totalSec : null;

    const setWindowStart = React.useCallback((next: number) => {
        setWindowStartSec(clampWindowStart(next, windowInfo))
    }, [windowInfo]);

    const pageBy = React.useCallback((direction: -1 | 1) => {
        setWindowStartSec(current => stepWindowStart(current, direction, windowInfo))
    }, [windowInfo]);

    const pageToEnd = React.useCallback((direction: -1 | 1) => {
        setWindowStartSec(direction < 0 ? 0 : windowInfo.maxStartSec)
    }, [windowInfo]);

    React.useEffect(() => {
        setWindowStartSec(0)
    }, [recordKey]);

    const openClinical = React.useCallback(() => {
        setClinicalAuto(false);
        setClinicalOpen(true)
    }, []);

    const closeClinical = React.useCallback(() => {
        setClinicalAuto(false);
        setClinicalOpen(false)
    }, []);

    // "Approve" and "Approve & next" are the same dialog; they differ only in
    // how `receiveNext` is pre-set, which is the flag ApproveDialog's checkbox
    // is bound to. The reviewer still confirms in the dialog either way.
    const openApprove = React.useCallback((receiveNext: boolean) => {
        if (!canApprove) {
            return
        }

        // Flush anything still sitting in a local draft so the reviewer is not
        // silently blocked by their own last keystroke. If a write is genuinely
        // in flight we still refuse — the toolbar buttons are disabled with a
        // reason while that is true, so this is only the keyboard path.
        if (dispoApiRef.current !== null) {
            dispoApiRef.current.saveNow()
        }

        /* annotation edits still on their way: send them now, and refuse until
           the server has them (the toolbar says why) */
        if (annotationUnsaved) {
            record.flush()
        }

        if (editsPending || annotationBlock !== null) {
            return
        }

        setReceiveNext(receiveNext);
        setApproveDialogActive(true)
    }, [canApprove, editsPending, annotationUnsaved, annotationBlock, record, setReceiveNext, setApproveDialogActive]);

    const handleApprove = React.useCallback(() => openApprove(false), [openApprove]);
    const handleApproveNext = React.useCallback(() => openApprove(true), [openApprove]);

    const handleMeasure = React.useCallback((next: EcgMeasurement) => setMeasurement(next), []);
    const handleCaliperClear = React.useCallback(() => setMeasurement(null), []);
    const clearMeasurement = React.useCallback(() => {
        const drawn = caliperApiRef.current !== null && caliperApiRef.current.clear();

        setMeasurement(null);

        return drawn
    }, []);

    const handleApplyMeasurement = React.useCallback((key: ParameterKey, value: number) => {
        if (paramsApiRef.current !== null) {
            paramsApiRef.current.apply(key, value)
        }

        clearMeasurement()
    }, [clearMeasurement]);

    useRecordShortcuts({
        onHome: () => history.push(AppRoute.Home),
        onInterpretation: index => {
            if (dispoApiRef.current) {
                dispoApiRef.current.pick(index)
            }
        },
        onToggleTheme: toggleTheme,
        onTogglePanel: () => setPanelOpen(open => !open),
        onToggleCalipers: () => setCaliperOn(on => !on),
        onToggleSidebar: () => setSidebar(on => !on),
        onZoomIn: ecgZoom.zoomIn,
        onZoomOut: ecgZoom.zoomOut,
        onZoomReset: ecgZoom.reset,
        onClinicalInfo: openClinical,
        onEscape: () => {
            if (clinicalOpen) {
                closeClinical();

                return
            }

            if (clearMeasurement() || measurement !== null) {
                return
            }

            setCaliperOn(false)
        },
        onSave: () => {
            if (dispoApiRef.current) {
                dispoApiRef.current.saveNow()
            }
        },
        onApprove: handleApproveNext,
        onPage: pageBy,
        onPageEnd: pageToEnd,
    }, {
        /*
         * Annotation brings its own key set and the bare keys overlap badly:
         * the arrows page the record here but nudge a boundary there, `+`/`-`
         * zoom here but change gain there, `[` toggles the panel, `o` opens
         * clinical info, and `1`-`6` set the interpretation where annotation
         * sets the beat class. Those are dropped.
         *
         * Ctrl+S and Ctrl+Enter stay live: nothing in annotation claims a
         * modifier combination, and keeping approve on the keyboard is what
         * lets its buttons come off the toolbar.
         */
        bareKeys: !annotationOn,
    });
    React.useEffect(() => {
        if (fetchingRecord || recordKey === null || !hasClinicalInfo) {
            return
        }

        if (clinicalShownForRef.current === recordKey) {
            return
        }

        clinicalShownForRef.current = recordKey;
        setClinicalAuto(true);
        setClinicalOpen(true)
    }, [fetchingRecord, recordKey, hasClinicalInfo]);
    React.useEffect(() => {
        if (!clinicalOpen || !clinicalAuto) {
            return undefined
        }

        let armed = false;
        let origin: {x: number, y: number} | null = null;

        const armTimeoutId = window.setTimeout(() => {
            armed = true
        }, CLINICAL_SETTLE_MS);

        function dismiss() {
            window.clearTimeout(armTimeoutId);
            setClinicalAuto(false);
            setClinicalOpen(false)
        }

        function handlePointerMove(event: PointerEvent) {
            if (!armed) {
                return
            }

            if (origin === null) {
                origin = {x: event.clientX, y: event.clientY};

                return
            }

            const dx = event.clientX - origin.x;
            const dy = event.clientY - origin.y;

            if (Math.sqrt((dx * dx) + (dy * dy)) >= CLINICAL_DISMISS_PX) {
                dismiss()
            }
        }

        function handleAction() {
            if (armed) {
                dismiss()
            }
        }

        window.addEventListener("pointermove", handlePointerMove, true);
        window.addEventListener("pointerdown", handleAction, true);
        window.addEventListener("keydown", handleAction, true);
        window.addEventListener("wheel", handleAction, true);
        window.addEventListener("scroll", handleAction, true);

        return () => {
            window.clearTimeout(armTimeoutId);
            window.removeEventListener("pointermove", handlePointerMove, true);
            window.removeEventListener("pointerdown", handleAction, true);
            window.removeEventListener("keydown", handleAction, true);
            window.removeEventListener("wheel", handleAction, true);
            window.removeEventListener("scroll", handleAction, true)
        }
    }, [clinicalOpen, clinicalAuto]);
    React.useEffect(() => {
        if (mobile) {
            setZoom(1)
        }
    }, [mobile]);

    // Plotly sizes itself from a window resize event; collapsing the info strip
    // or switching to the rail changes the stage's box without one, leaving the
    // trace letterboxed. `mobileTab` is in here for the same reason and matters
    React.useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            try {
                window.dispatchEvent(new Event("resize"))
            } catch (error) {
                // older embedded Safari has no Event constructor
                const legacy = document.createEvent("HTMLEvents");
                legacy.initEvent("resize", true, false);
                window.dispatchEvent(legacy)
            }
        }, 60);

        return () => window.clearTimeout(timeoutId)
    }, [panelOpen, sidebar, mobile, mobileTab]);
    React.useEffect(() => {
        if (!caliperOn) {
            setMeasurement(null)
        }
    }, [caliperOn]);

    // A reading taken at one speed/gain/layout does not describe the trace at
    // the next one, so it must not survive the change — and neither may the
    React.useEffect(() => {
        clearMeasurement()
    }, [layout, speed, gain, windowStartSec, clearMeasurement]);
    function handleBack() {
        if (history.length > 1) {
            history.goBack();

            return
        }

        history.replace(AppRoute.Home)
    }

    function handleEdit() {
        if (currentRecord === null) {
            return
        }

        const url = `cardiolyse://holter?ecgId=${currentRecord.ecgId}&teleRecordId=${currentRecord.teleRecordId}`;
        const win = window.open(url, "_blank");

        if (win) {
            win.focus()
        }
    }

    function renderStatusChip() {
        if (currentRecord === null) {
            return null
        }

        const map: {[key: string]: {className: string, labelKey: string}} = {
            [RecordStatusEnum.NEW]: {className: styles.chip__new, labelKey: "modern.status_new"},
            [RecordStatusEnum.IN_PROGRESS]: {className: styles.chip__progress, labelKey: "modern.status_in_progress"},
            [RecordStatusEnum.APPROVED]: {className: styles.chip__approved, labelKey: "modern.status_approved"},
        };
        const variant = map[currentRecord.status];

        if (!variant) {
            return null
        }

        return (
            <span className={`${styles.chip} ${variant.className}`}>
                <i className={styles.dot}/>
                {t(variant.labelKey)}
            </span>
        )
    }

    function renderMeta() {
        const pediatric = isPediatric(metaData, patientInfo);

        return (
            <div className={styles.recmeta}>
                <ModernBackButton confirm={isEditable} onBack={handleBack}/>
                <span className={styles.fname} title={currentRecord ? currentRecord.fileName : undefined}>
                    {currentRecord ? currentRecord.fileName : "—"}
                </span>
                {renderStatusChip()}
                {pediatric && (
                    <span className={`${styles.chip} ${styles.chip__pediatric}`}>{t("records.pediatric")}</span>
                )}
                {readOnly && <span className={styles.chip}>{t("modern.read_only")}</span>}
            </div>
        )
    }

    function renderStage() {
        if (currentRecord === null || currentRecord.recordType !== RecordTypeEnum.ECG) {
            return <div className={styles.stage_message}>{t("modern.no_signal")}</div>
        }

        if (signalsFetching) {
            return <div className={styles.stage_message}>{t("modern.loading")}</div>
        }

        if (!signals || signals.length === 0) {
            return <div className={styles.stage_message}>{t("modern.no_signal")}</div>
        }

        // Annotation replaces the review stage rather than sitting beside it:
        // the two want the same screen and the same time axis, and the beat
        // surface needs its own scale — roughly 0.7 ms to the pixel, against the
        // stage's 10.6 — for a boundary to be placeable at all.
        if (annotationOn) {
            return (
                <AnnotationView
                    /*
                     * The WHOLE record, not the review stage's page of it.
                     *
                     * `stageSignals` is `legacyWindowSignals(...)`, which caps at
                     * 15 s and then keeps 17/25 of that — about 10.2 s. The beat
                     * list, though, is built from the full signal, so on any
                     * longer record the cursor walks onto beats whose trace was
                     * never handed to this view: the marks are editable but there
                     * is nothing to judge them against, which is the entire task.
                     * This surface does its own paging by scrolling, so it has no
                     * use for the stage's window.
                     */
                    signals={signals || []}
                    beats={annotation.beats}
                    current={annotation.current}
                    selected={annotation.selected}
                    onSelect={annotation.select}
                    onMove={annotation.move}
                    savedAt={record.savedAt}
                    saveFailed={record.localFailed}
                    savedTo={record.savedTo}
                    labelsStatus={record.labelsStatus}
                    sampleRate={record.rate.rate}
                    rateProblem={record.rate.problem}
                    serverRate={record.serverRate}
                    conflicts={record.conflicts}
                    saveFailing={record.failing}
                    saveRefused={record.refused ? record.saveStatus : null}
                    onDragStart={annotation.beginDrag}
                    onDragEnd={annotation.endDrag}
                />
            )
        }

        // Theme-aware equivalent of Classic's <ECGGraph/>: same withRecordSignals
        // wiring and identical default geometry, but the Plotly surface follows
        // the reading-room tokens instead of being hard-coded white, and it
        // honours the toolbar's layout / speed / gain.
        return (
            <ModernEcgStage
                layout={layout}
                speed={speed}
                gain={gain}
                windowStartSec={windowStartSec}
                longRecords={longRecords}
                zoom={zoom}
                boxRef={ecgBoxRef}
                caliperOn={caliperOn}
                caliperApiRef={caliperApiRef}
                onMeasure={handleMeasure}
                onCaliperClear={handleCaliperClear}
            />
        )
    }

    /**
     * The trace plus everything that floats over it. The caliper hint and the
     * measure bar sit OUTSIDE the scrolling stage, so they stay put while the
     * reviewer scrolls down a 12-lead figure.
     */
    function renderStageArea() {
        const showPager =
            windowInfo.paged &&
            !signalsFetching &&
            currentRecord !== null &&
            currentRecord.recordType === RecordTypeEnum.ECG;

        return (
            <React.Fragment>
                <div className={styles.stage_wrap}>
                    <div
                        className={`${styles.stage} ${caliperOn ? "" : styles.stage__pan}`}
                        ref={ecgZoom.stageRef}
                    >
                        {renderStage()}
                    </div>

                    {caliperOn && measurement === null && (
                        <div className={styles.caliper_hint}>{t("modern.caliper_hint")}</div>
                    )}

                    {measurement !== null && (
                        <ModernMeasureBar
                            measurement={measurement}
                            editable={isEditable}
                            onApply={handleApplyMeasurement}
                            onClose={clearMeasurement}
                        />
                    )}
                </div>

                {showPager && (
                    <ModernEcgPager
                        startSec={windowStartSec}
                        info={windowInfo}
                        onChange={setWindowStart}
                    />
                )}
            </React.Fragment>
        )
    }

    function renderDispo(variant: "card" | "dock") {
        return (
            <ModernDispoCard
                editable={isEditable}
                recordId={currentRecord !== null ? (currentRecord.teleRecordId || currentRecord.ecgId || null) : null}
                apiRef={dispoApiRef}
                draftCacheRef={conclusionDraftRef}
                onBusyChange={setDispoBusy}
                /*
                 * The findings field can expand in place to show a whole
                 * report at once. It cannot do that alone: `topinfo` caps the
                 * band at 44vh and each card scrolls inside it, so a taller
                 * textarea would only scroll rather than show more. The card
                 * says when it is expanded and the cap is lifted here.
                 */
                onExpandChange={setFindingsExpanded}
                variant={variant}
                mobile={mobile}
            />
        )
    }

    function renderBody() {
        if (fetchingRecord) {
            return (
                <div className={styles.centered}>
                    <span className={styles.centered_note}>{t("modern.loading")}</span>
                </div>
            )
        }

        if (currentRecord === null) {
            return (
                <div className={styles.centered}>
                    <span className={styles.centered_title}>{t("records.nothing_to_review")}</span>
                    <button type="button" className={styles.cta} onClick={() => history.replace(AppRoute.Home)}>
                        {t("back")}
                    </button>
                </div>
            )
        }

        return (
            <React.Fragment>
                {mobile ? renderMobileBody() : renderDesktopBody()}
                <ModernClinicalDialog
                    open={clinicalOpen}
                    record={currentRecord}
                    onClose={closeClinical}
                />
            </React.Fragment>
        )
    }
    function renderToolbar(compact: boolean) {
        if (currentRecord === null) {
            return null
        }

        return (
            <ModernRecordToolbar
                panelOpen={panelOpen}
                onTogglePanel={() => setPanelOpen(open => !open)}
                layout={layout}
                onLayoutChange={setLayout}
                layoutDisabled={windowInfo.strip}
                speed={speed}
                onSpeedChange={setSpeed}
                gain={gain}
                onGainChange={setGain}
                zoom={zoom}
                onZoomIn={ecgZoom.zoomIn}
                onZoomOut={ecgZoom.zoomOut}
                onZoomReset={ecgZoom.reset}
                caliperOn={caliperOn}
                onToggleCalipers={() => setCaliperOn(on => !on)}
                showAnnotation={
                    /*
                     * Any record with a trace, NOT only one that already has
                     * marks: the view itself says why there is nothing to edit
                     * — no marks stored for this ECG yet, a sample rate that is
                     * missing or mixed, or marks stored at another rate.
                     */
                    !!signals && signals.length > 0
                }
                annotationOn={annotationOn}
                onToggleAnnotation={toggleAnnotation}
                sidebar={sidebar}
                onToggleSidebar={() => setSidebar(on => !on)}
                showApprove={canApprove && !annotationOn}
                approveDisabled={editsPending || annotationBlock !== null}
                approveBlockedReason={annotationBlock || undefined}
                onApprove={handleApprove}
                onApproveNext={handleApproveNext}
                showEdit={
                    currentRecord.recordType === RecordTypeEnum.HOLTER &&
                    Boolean(currentRecord.ecgId) &&
                    Boolean(currentRecord.teleRecordId)
                }
                onEdit={handleEdit}
                compact={compact}
            />
        )
    }
    function renderMobileBody() {
        /* Same reasoning as the desktop branch, and more so: there is barely
           room for twelve leads on a phone as it is. */
        if (annotationOn) {
            return (
                <React.Fragment>
                    {renderToolbar(true)}

                    <div className={`${styles.main} ${styles.main__annotation}`}>
                        {renderStage()}
                    </div>
                </React.Fragment>
            )
        }

        return (
            <ModernRecordMobile
                toolbar={renderToolbar(true)}
                stage={renderStageArea()}
                details={
                    <React.Fragment>
                        <ModernPatientCard onOpenClinical={openClinical} durationSec={durationSec}/>
                        <ModernParamsCard
                            editable={isEditable}
                            apiRef={paramsApiRef}
                            onBusyChange={setParamsBusy}
                        />
                    </React.Fragment>
                }
                conclusion={renderDispo("card")}
                tab={mobileTab}
                onTabChange={setMobileTab}
                showApprove={canApprove}
                approveDisabled={editsPending || annotationBlock !== null}
                onApprove={handleApprove}
                onApproveNext={handleApproveNext}
            />
        )
    }

    function renderDesktopBody() {
        /*
         * Annotation gets the screen to itself.
         *
         * Patient details, parameters, conclusion and interpretation all belong
         * to reporting a record, not to marking where a wave begins — and the
         * beat surface is the one view that genuinely wants the height: twelve
         * leads have to share one time axis for a boundary to be judged across
         * them. The reviewer keys are gone with them, because none of them
         * apply here.
         */
        if (annotationOn) {
            return (
                <React.Fragment>
                    {renderToolbar(false)}

                    <div className={`${styles.main} ${styles.main__annotation}`}>
                        {renderStage()}
                    </div>
                </React.Fragment>
            )
        }

        return (
            <React.Fragment>
                {renderToolbar(false)}

                <div className={`${styles.main} ${sidebar ? styles.main__sidebar : ""}`}>
                    {sidebar ? (
                        <React.Fragment>
                            <aside className={`${styles.rail} ${panelOpen ? "" : styles.collapsed}`}>
                                <ModernPatientCard onOpenClinical={openClinical} durationSec={durationSec}/>
                                <ModernParamsCard
                                    editable={isEditable}
                                    apiRef={paramsApiRef}
                                    onBusyChange={setParamsBusy}
                                />
                            </aside>

                            <div className={styles.stage_column}>
                                {renderStageArea()}
                                <div className={styles.dock}>{renderDispo("dock")}</div>
                            </div>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            {/* The band itself does not change while the
                              * findings are expanded — the card hangs out of
                              * it, so the trace keeps its position. */}
                            <div className={`${styles.topinfo} ${panelOpen ? "" : styles.collapsed}`}>
                                <ModernPatientCard onOpenClinical={openClinical} durationSec={durationSec}/>
                                <ModernParamsCard
                                    editable={isEditable}
                                    apiRef={paramsApiRef}
                                    onBusyChange={setParamsBusy}
                                />
                                {/* The findings card overhangs its own column
                                  * when expanded rather than growing the band.
                                  * This cell is what it hangs from. */}
                                <div
                                    className={
                                        `${styles.dispo_cell}` +
                                        `${findingsExpanded ? " " + styles.dispo_cell__expanded : ""}`
                                    }
                                >
                                    {renderDispo("card")}
                                </div>
                            </div>

                            {renderStageArea()}
                        </React.Fragment>
                    )}
                </div>

                <ModernKeysBar showEditingKeys={isEditable} showPagingKeys={windowInfo.paged}/>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <ModernAppBar>
                {renderMeta()}
            </ModernAppBar>
            <div className={`${styles.shell} ${mobile ? styles.shell__mobile : ""}`}>
                {renderBody()}
            </div>

            {/*
              * The Modern-skinned approve confirmation. Same store wiring and
              * the same render-phase `history.replace` as Classic's
              * <ApproveDialog/>, which Classic still uses untouched — so this
              * tree must never be wrapped in StrictMode.
              *
              * Mounted for the whole live-review route rather than gated on
              * `canApprove`, and OUTSIDE renderBody() so it survives
              * `currentRecord` going null. Approving without "receive next"
              * nulls the record in the same commit that would have unmounted
              * this — taking its render-phase `history.replace(AppRoute.Home)`
              * with it and stranding the reviewer on the "nothing to review"
              * screen. With no record and the dialog closed it renders nothing,
              * so mounting it unconditionally on this route costs nothing.
              */}
            {false === readOnly && <ModernApproveDialog/>}
        </React.Fragment>
    )
}

export {ModernRecordPage}

export default compose(
    withRecordSignals,
    withApproveDialog,
)(ModernRecordPage) as React.ComponentType<any>
